<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/dropdown-menu'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import aiStarsLogo from '@/assets/ai_stars_color.svg'
import { useUserStore } from '@/authentication/user.store'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import { betaOnlyLanguageNames, languageMenuLabel, releasedLanguageNames } from './translation_languages'
import type { TranslationLanguage } from './translation_types'

const props = defineProps<{
  languages: TranslationLanguage[]
}>()

const emit = defineEmits<{
  translationLanguageChanged: [language: string]
}>()

const userStore = useUserStore()
const { isTrial, isLicenseExpired, authInfo } = storeToRefs(userStore)
const { betaVisibility } = storeToRefs(useVisibilityStore())

const isMenuOpen = ref(false)
const isLimitModalOpen = ref(false)
const selectedLanguage = ref('')

const isTranslationBlocked = computed(() => Boolean(isTrial.value || isLicenseExpired.value))
const permissions = computed(() => actionPermissions(ACTION_TYPES.TRANSLATE, CONTENT_TYPES.GOAL, {}, authInfo.value))

const menuLanguages = computed(() => {
  const released = releasedLanguageNames(props.languages)
  if (!betaVisibility.value) {
    return released
  }
  return [...new Set([...released, ...betaOnlyLanguageNames(props.languages)])]
})

function handleMenuOpenChange(isOpening: boolean) {
  if (isOpening && isTranslationBlocked.value) {
    isLimitModalOpen.value = true
    return
  }
  isMenuOpen.value = isOpening
}

function selectLanguage(language: string) {
  selectedLanguage.value = language
  emit('translationLanguageChanged', language)
}
</script>

<template>
  <div class="inline-block">
    <!-- non-modal: a modal menu marks the rest of the page aria-hidden while it is open,
         which axe reports as aria-hidden-focus -->
    <DropdownMenu :open="isMenuOpen" :modal="false" @update:open="handleMenuOpenChange">
      <DropdownMenuTrigger
        data-testid="translate-button"
        class="inline-flex items-center whitespace-nowrap rounded-xs border border-gray-600 bg-white px-1 py-0.5 text-sm leading-normal text-gray-900 hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400 data-[state=open]:bg-gray-300 data-[state=open]:shadow-inner"
      >
        <img class="mr-1 h-3 w-2.5" :src="aiStarsLogo" alt="AI Powered Feature">Translate Goal
        <span class="material-icons-round ml-1 text-lg leading-none" aria-hidden="true">arrow_drop_down</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        :side-offset="2"
        class="min-w-32 rounded-xs border-gray-400 bg-white px-0 py-1 shadow-lg"
      >
        <DropdownMenuRadioGroup :model-value="selectedLanguage">
          <DropdownMenuRadioItem
            v-for="language in menuLanguages"
            :key="language"
            :value="language"
            class="cursor-pointer rounded-none py-1 px-5 text-sm leading-normal text-gray-900 focus:bg-gray-100"
            @select="selectLanguage(language)"
          >
            {{ languageMenuLabel(language) }}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
    <PremiumActionModal
      v-if="isTranslationBlocked"
      v-model:external-open="isLimitModalOpen"
      :action-permissions="permissions"
    />
  </div>
</template>
